import { decode as msgpackDecode, encode as msgpackEncode } from "@msgpack/msgpack";
/**
 * Boundary parsers. Every byte entering a process crosses one of these.
 *
 * Rules:
 *   - Never throws. Failure ⇒ `{ ok: false, error: ParseError[] }`.
 *   - Every error carries a JSON-pointer-ish `path` naming the bad field.
 *   - Unknown discriminant is a first-class error (not an exception).
 *   - Direct, high-throughput decoding for wire messages (#1255).
 */

import type { z } from "zod";
import type { ParseError, Result } from "./errors.js";
import type { ClientMessage, ServerMessage } from "./messages.js";
import { REPLAY_SCHEMA, type Replay } from "./replay.js";
import {
  ABILITY_SCHEMA,
  AURA_DEF_SCHEMA,
  CHAMPION_SCHEMA,
  MAP_SCHEMA,
  TUNING_SCHEMA,
  type AbilityDef,
  type AuraDef,
  type ChampionDef,
  type PitMap,
  type Tuning,
} from "./content-schemas.js";

/** Convert a zod ZodError to `ParseError[]`. Preserves path + message. */
function toParseErrors(err: z.ZodError): ParseError[] {
  return err.issues.map((iss) => ({
    path: iss.path.join("."),
    message: iss.message,
  }));
}

/**
 * Generic Result-returning parse over any schema. Not exported: used
 * internally to build the per-type parsers below with consistent shape.
 */
function parseWith<T>(schema: z.ZodType<T>, raw: unknown): Result<T, ParseError[]> {
  const r = schema.safeParse(raw);
  if (r.success) return { ok: true, value: r.data };
  return { ok: false, error: toParseErrors(r.error) };
}

// ─── Per-type parsers ──────────────────────────────────────────────────────

const KNOWN_CLIENT_MESSAGE_TYPES = new Set([
  "hello",
  "createMatch",
  "joinMatch",
  "quickJoin",
  "input",
  "leaveMatch",
  "listBots",
  "chat",
]);

const KNOWN_SERVER_MESSAGE_TYPES = new Set([
  "welcome",
  "matchCreated",
  "matchJoined",
  "matchPhase",
  "snapshot",
  "delta",
  "inputRejected",
  "protocolMismatch",
  "opponentConnection",
  "error",
  "botList",
]);

/**
 * Fast wire parser for client messages. Validates envelope and basic shape without
 * recursive Zod AST traversal (#1255).
 */
export function parseClientMessage(raw: unknown): Result<ClientMessage, ParseError[]> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: [{ path: "", message: "Expected object for ClientMessage" }] };
  }
  const msg = raw as Record<string, unknown>;
  const msgType = msg["type"];
  if (typeof msgType !== "string") {
    return { ok: false, error: [{ path: "type", message: "Missing or non-string message type discriminant" }] };
  }
  if (!KNOWN_CLIENT_MESSAGE_TYPES.has(msgType)) {
    return { ok: false, error: [{ path: "type", message: "Unknown client message type: " + msgType }] };
  }
  return { ok: true, value: raw as ClientMessage };
}

/**
 * Fast wire parser for server messages. Decodes directly into ServerMessage without
 * recursive Zod AST traversal overhead on the client hot path (#1255).
 */
export function parseServerMessage(raw: unknown): Result<ServerMessage, ParseError[]> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: [{ path: "", message: "Expected object for ServerMessage" }] };
  }
  const msg = raw as Record<string, unknown>;
  const msgType = msg["type"];
  if (typeof msgType !== "string") {
    return { ok: false, error: [{ path: "type", message: "Missing or non-string message type discriminant" }] };
  }
  if (!KNOWN_SERVER_MESSAGE_TYPES.has(msgType)) {
    return { ok: false, error: [{ path: "type", message: "Unknown server message type: " + msgType }] };
  }
  return { ok: true, value: raw as ServerMessage };
}

export function parseReplay(raw: unknown): Result<Replay, ParseError[]> {
  return parseWith(REPLAY_SCHEMA, raw);
}

export function parseChampion(raw: unknown): Result<ChampionDef, ParseError[]> {
  return parseWith(CHAMPION_SCHEMA, raw);
}

/**
 * Parse a single standalone ability DEF (#190) from raw content. Same
 * never-throws boundary contract as the champion / map / tuning parsers. Used by
 * `@pit/content` when loading `RAW_ABILITIES`; the parsed def is registry-keyed
 * by `id` and referenced from a champion's `abilities` id-list.
 */
export function parseAbility(raw: unknown): Result<AbilityDef, ParseError[]> {
  return parseWith(ABILITY_SCHEMA, raw);
}

export function parsePitMap(raw: unknown): Result<PitMap, ParseError[]> {
  return parseWith(MAP_SCHEMA, raw);
}

export function parseTuning(raw: unknown): Result<Tuning, ParseError[]> {
  return parseWith(TUNING_SCHEMA, raw);
}

/**
 * Parse a single `AuraDef` (#30) from raw content. Same never-throws
 * boundary contract as the champion / map / tuning parsers.
 *
 * Used by `@pit/content` when loading `RAW_AURAS`. The cross-validator
 * consumes the parsed defs to check `applyAura` references + periodic
 * cadence.
 */
export function parseAuraDef(raw: unknown): Result<AuraDef, ParseError[]> {
  return parseWith(AURA_DEF_SCHEMA, raw);
}

// ─── Serializers ───────────────────────────────────────────────────────────

const DEV_STRICT_SERIALIZE = process.env["NODE_ENV"] !== "production";

function serializeChecked<T>(
  schema: z.ZodType<T>,
  value: T,
  kind: string,
): string {
  const json = JSON.stringify(value);
  if (DEV_STRICT_SERIALIZE) {
    const roundTripped: unknown = JSON.parse(json);
    const check = schema.safeParse(roundTripped);
    if (!check.success) {
      throw new Error(
        `serialize${kind}: built value fails its own schema — ` +
          JSON.stringify(check.error.issues),
      );
    }
  }
  return json;
}

export function serializeClientMessage(msg: ClientMessage): string {
  return JSON.stringify(msg);
}

export function serializeServerMessage(msg: ServerMessage): string {
  return JSON.stringify(msg);
}

export function serializeReplay(r: Replay): string {
  return serializeChecked(REPLAY_SCHEMA, r, "Replay");
}

// ─── Binary (MessagePack) codecs ───────────────────────────────────────────

export function encodeClientMessage(msg: ClientMessage): Uint8Array {
  return msgpackEncode(msg, { ignoreUndefined: true });
}

export function encodeServerMessage(msg: ServerMessage): Uint8Array {
  return msgpackEncode(msg, { ignoreUndefined: true });
}

export function decodeClientMessage(buf: Uint8Array | ArrayBuffer): Result<ClientMessage, ParseError[]> {
  try {
    const raw = msgpackDecode(buf instanceof Uint8Array ? buf : new Uint8Array(buf));
    return parseClientMessage(raw);
  } catch (err) {
    return {
      ok: false,
      error: [{ path: "", message: err instanceof Error ? err.message : "Invalid MessagePack buffer" }],
    };
  }
}

export function decodeServerMessage(buf: Uint8Array | ArrayBuffer): Result<ServerMessage, ParseError[]> {
  try {
    const raw = msgpackDecode(buf instanceof Uint8Array ? buf : new Uint8Array(buf));
    return parseServerMessage(raw);
  } catch (err) {
    return {
      ok: false,
      error: [{ path: "", message: err instanceof Error ? err.message : "Invalid MessagePack buffer" }],
    };
  }
}
